import { useState, useEffect } from 'react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { X, Key, Link2, CheckCircle, AlertTriangle, ExternalLink, Loader2 } from 'lucide-react'
import { googleAdsApi, GoogleAdsConfig } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/lib/utils'

interface GoogleAdsConfigModalProps {
  isOpen: boolean
  onClose: () => void
  config?: GoogleAdsConfig
  onConfigured: () => void
}

export default function GoogleAdsConfigModal({
  isOpen,
  onClose,
  config,
  onConfigured
}: GoogleAdsConfigModalProps) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [activeTab, setActiveTab] = useState<'credentials' | 'oauth'>('credentials')

  // Credentials form
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [developerToken, setDeveloperToken] = useState('')
  const [customerId, setCustomerId] = useState('')
  const [loginCustomerId, setLoginCustomerId] = useState('')

  // OAuth state
  const [oauthUrl, setOauthUrl] = useState<string | null>(null)
  const [oauthCode, setOauthCode] = useState('')

  // Get current status
  const statusQuery = useQuery({
    queryKey: ['google-ads-status'],
    queryFn: googleAdsApi.getStatus,
    enabled: isOpen
  })

  // Mutations
  const setCredentialsMutation = useMutation({
    mutationFn: googleAdsApi.setCredentials,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-status'] })
      queryClient.invalidateQueries({ queryKey: ['google-ads-config'] })
      toast.success(data.message || 'Credenciales guardadas')
      // Clear form
      setClientId('')
      setClientSecret('')
      setDeveloperToken('')
      // Move to OAuth tab
      setActiveTab('oauth')
    },
    onError: (error: any) => {
      toast.error('Error guardando credenciales', error.response?.data?.detail || error.message)
    }
  })

  const getOAuthUrlMutation = useMutation({
    mutationFn: () => {
      const redirectUri = `${window.location.origin}/google-ads`
      return googleAdsApi.getOAuthUrl(redirectUri)
    },
    onSuccess: (data) => {
      setOauthUrl(data.authorization_url)
    },
    onError: (error: any) => {
      toast.error('Error generando URL OAuth', error.response?.data?.detail || error.message)
    }
  })

  const completeOAuthMutation = useMutation({
    mutationFn: (code: string) => {
      const redirectUri = `${window.location.origin}/google-ads`
      return googleAdsApi.completeOAuth(code, redirectUri)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-status'] })
      queryClient.invalidateQueries({ queryKey: ['google-ads-config'] })
      toast.success(data.message || 'OAuth completado correctamente')
      setOauthCode('')
      setOauthUrl(null)
      onConfigured()
    },
    onError: (error: any) => {
      toast.error('Error completando OAuth', error.response?.data?.detail || error.message)
    }
  })

  // Check for OAuth callback in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')

    if (code && isOpen) {
      setOauthCode(code)
      setActiveTab('oauth')
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [isOpen])

  const handleSetCredentials = (e: React.FormEvent) => {
    e.preventDefault()
    if (!clientId.trim() || !clientSecret.trim() || !developerToken.trim() || !customerId.trim()) {
      toast.error('Error', 'Todos los campos son requeridos')
      return
    }
    setCredentialsMutation.mutate({
      client_id: clientId.trim(),
      client_secret: clientSecret.trim(),
      developer_token: developerToken.trim(),
      customer_id: customerId.trim(),
      login_customer_id: loginCustomerId.trim() || undefined
    })
  }

  const handleCompleteOAuth = (e: React.FormEvent) => {
    e.preventDefault()
    if (!oauthCode.trim()) {
      toast.error('Error', 'El codigo de autorizacion es requerido')
      return
    }
    completeOAuthMutation.mutate(oauthCode.trim())
  }

  const isConnected = statusQuery.data?.connected === true
  const isConfigured = config?.is_configured === true
  const credentials = config?.credentials

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-slate-500/75 dark:bg-slate-900/75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-slate-800 rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Configuracion de Google Ads
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-slate-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-200 dark:border-slate-700">
            <button
              onClick={() => setActiveTab('credentials')}
              className={cn(
                "flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                activeTab === 'credentials'
                  ? "border-primary-500 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-slate-500 hover:text-gray-700 dark:text-slate-400"
              )}
            >
              <Key className="w-4 h-4 inline-block mr-2" />
              Credenciales
            </button>
            <button
              onClick={() => setActiveTab('oauth')}
              className={cn(
                "flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                activeTab === 'oauth'
                  ? "border-primary-500 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-slate-500 hover:text-gray-700 dark:text-slate-400"
              )}
            >
              <Link2 className="w-4 h-4 inline-block mr-2" />
              Autorizacion OAuth
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {activeTab === 'credentials' && (
              <div className="space-y-6">
                {/* Current Status */}
                <div className={cn(
                  "p-4 rounded-lg border",
                  isConnected
                    ? "bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800"
                    : isConfigured
                    ? "bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800"
                    : "bg-slate-50 border-gray-200 dark:bg-slate-800 dark:border-gray-700"
                )}>
                  <div className="flex items-center gap-3">
                    {isConnected ? (
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                    ) : isConfigured ? (
                      <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                    ) : (
                      <Key className="w-5 h-5 text-slate-400" />
                    )}
                    <div>
                      <p className={cn(
                        "font-medium",
                        isConnected
                          ? "text-green-800 dark:text-green-200"
                          : isConfigured
                          ? "text-yellow-800 dark:text-yellow-200"
                          : "text-slate-700 dark:text-slate-300"
                      )}>
                        {isConnected
                          ? 'Conectado a Google Ads'
                          : isConfigured
                          ? 'Credenciales configuradas - Pendiente OAuth'
                          : 'No configurado'
                        }
                      </p>
                      {isConnected && statusQuery.data?.account_name && (
                        <p className="text-sm text-green-600 dark:text-green-400">
                          {statusQuery.data.account_name} ({statusQuery.data.customer_id})
                        </p>
                      )}
                      {credentials && (
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 space-y-0.5">
                          <p>Client ID: {credentials.client_id_set ? 'Configurado' : 'No configurado'}</p>
                          <p>Developer Token: {credentials.developer_token_set ? 'Configurado' : 'No configurado'}</p>
                          <p>Customer ID: {credentials.customer_id || 'No configurado'}</p>
                          <p>OAuth: {credentials.refresh_token_set ? 'Completado' : 'Pendiente'}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Set Credentials Form */}
                <form onSubmit={handleSetCredentials} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Client ID *
                    </label>
                    <input
                      type="text"
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                      placeholder="xxxx.apps.googleusercontent.com"
                      className="input w-full font-mono text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Client Secret *
                    </label>
                    <input
                      type="password"
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                      placeholder="GOCSPX-..."
                      className="input w-full font-mono text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Developer Token *
                    </label>
                    <input
                      type="password"
                      value={developerToken}
                      onChange={(e) => setDeveloperToken(e.target.value)}
                      placeholder="xxxx-xxxx-xxxx"
                      className="input w-full font-mono text-sm"
                    />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Obtenlo en Google Ads API Center
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Customer ID (MCC o cuenta) *
                    </label>
                    <input
                      type="text"
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      placeholder="123-456-7890"
                      className="input w-full font-mono text-sm"
                    />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      ID de tu cuenta de Google Ads (sin guiones)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Login Customer ID (opcional, para MCC)
                    </label>
                    <input
                      type="text"
                      value={loginCustomerId}
                      onChange={(e) => setLoginCustomerId(e.target.value)}
                      placeholder="123-456-7890"
                      className="input w-full font-mono text-sm"
                    />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Solo si accedes via cuenta MCC
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={setCredentialsMutation.isPending}
                    className="btn btn-primary w-full"
                  >
                    {setCredentialsMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      'Guardar Credenciales'
                    )}
                  </button>
                </form>

                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                    Como obtener las credenciales
                  </h4>
                  <ol className="text-sm text-blue-700 dark:text-blue-300 list-decimal list-inside space-y-1">
                    <li>Ve a Google Cloud Console y crea un proyecto</li>
                    <li>Habilita Google Ads API</li>
                    <li>Crea credenciales OAuth 2.0 (tipo "Aplicacion web")</li>
                    <li>Anade {window.location.origin}/google-ads como URI de redireccion</li>
                    <li>Solicita un Developer Token en Google Ads API Center</li>
                  </ol>
                </div>
              </div>
            )}

            {activeTab === 'oauth' && (
              <div className="space-y-6">
                {!isConfigured ? (
                  <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                    <Key className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Configura primero las credenciales en la pestana anterior</p>
                  </div>
                ) : isConnected ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                      Conectado correctamente
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400">
                      Tu cuenta de Google Ads esta conectada y lista para usar.
                    </p>
                    {statusQuery.data?.account_name && (
                      <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">
                        Cuenta: {statusQuery.data.account_name}
                      </p>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Step 1: Get OAuth URL */}
                    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                      <h4 className="font-medium text-slate-900 dark:text-white mb-3">
                        Paso 1: Autorizar acceso
                      </h4>

                      {!oauthUrl ? (
                        <button
                          onClick={() => getOAuthUrlMutation.mutate()}
                          disabled={getOAuthUrlMutation.isPending}
                          className="btn btn-primary w-full"
                        >
                          {getOAuthUrlMutation.isPending ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Generando...
                            </>
                          ) : (
                            <>
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Generar enlace de autorizacion
                            </>
                          )}
                        </button>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            Haz clic en el boton para autorizar el acceso a tu cuenta de Google Ads.
                            Seras redirigido a Google para iniciar sesion.
                          </p>
                          <a
                            href={oauthUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-primary w-full inline-flex items-center justify-center"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Abrir autorizacion en Google
                          </a>
                          <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
                            Despues de autorizar, copia el codigo que aparece y pegalo abajo.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Step 2: Enter Authorization Code */}
                    <form onSubmit={handleCompleteOAuth} className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                      <h4 className="font-medium text-slate-900 dark:text-white mb-3">
                        Paso 2: Completar autorizacion
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            Codigo de autorizacion
                          </label>
                          <input
                            type="text"
                            value={oauthCode}
                            onChange={(e) => setOauthCode(e.target.value)}
                            placeholder="4/xxxxx..."
                            className="input w-full font-mono text-sm"
                          />
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            Pega aqui el codigo que Google te proporciono despues de autorizar
                          </p>
                        </div>
                        <button
                          type="submit"
                          disabled={completeOAuthMutation.isPending || !oauthCode.trim()}
                          className="btn btn-primary w-full"
                        >
                          {completeOAuthMutation.isPending ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Completando...
                            </>
                          ) : (
                            'Completar conexion'
                          )}
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <button
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
